import api from './api';

const LOCAL_DRAFT_PREFIX = 'payment_draft';

const buildLocalKey = (userId) => (userId ? `${LOCAL_DRAFT_PREFIX}_${userId}` : LOCAL_DRAFT_PREFIX);

const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

const extractErrorMessage = (error) => {
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return 'Something went wrong while communicating with the server.';
};

const isNetworkOrRouteMissing = (error) => {
  if (!error?.response) return true; // Network error — no response
  const { status, data } = error.response;
  if (status === 404) {
    const message = (data?.message || '').toLowerCase();
    return message.includes('route not found');
  }
  return false;
};

const getLocalDraft = (userId) => {
  try {
    const stored = localStorage.getItem(buildLocalKey(userId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const setLocalDraft = (userId, draft) => {
  try {
    if (!draft) {
      localStorage.removeItem(buildLocalKey(userId));
      return null;
    }
    const payload = { ...draft, id: draft.id || `local-${Date.now()}`, _isLocal: true };
    localStorage.setItem(buildLocalKey(userId), JSON.stringify(payload));
    return payload;
  } catch {
    throw new Error('Unable to save draft locally.');
  }
};

const paymentDraftService = {
  async listDrafts(userId, params = {}) {
    try {
      const response = await api.get('/payments/drafts', { params });
      const drafts = unwrap(response);
      // If server returned data, also clear any stale local backup
      localStorage.removeItem(buildLocalKey(userId));
      return Array.isArray(drafts) ? drafts : [];
    } catch (error) {
      if (isNetworkOrRouteMissing(error)) {
        console.warn('Payment drafts API unavailable, reading from local backup.');
        const draft = getLocalDraft(userId);
        return draft ? [draft] : [];
      }
      throw new Error(extractErrorMessage(error));
    }
  },

  async getDraft(userId, draftId) {
    try {
      const response = await api.get(`/payments/drafts/${draftId}`);
      return unwrap(response);
    } catch (error) {
      if (isNetworkOrRouteMissing(error)) {
        console.warn('Payment drafts API unavailable, reading from local backup.');
        const draft = getLocalDraft(userId);
        return draft && (!draftId || draft.id === draftId) ? draft : null;
      }
      throw new Error(extractErrorMessage(error));
    }
  },

  async saveDraft(userId, draftPayload) {
    if (!draftPayload || !draftPayload.paymentData || !draftPayload.lineItems) {
      throw new Error('Draft is missing required fields.');
    }

    try {
      const response = await api.post('/payments/drafts', draftPayload);
      // Server save succeeded — remove any local backup to avoid stale reads
      localStorage.removeItem(buildLocalKey(userId));
      return unwrap(response);
    } catch (error) {
      if (isNetworkOrRouteMissing(error)) {
        // Network offline or route missing — save locally as a temporary backup
        console.warn('Payment drafts API unavailable, saving to local backup.');
        return setLocalDraft(userId, draftPayload);
      }
      throw new Error(extractErrorMessage(error));
    }
  },

  async deleteDraft(userId, draftId) {
    // Always try to clean up local backup regardless of API success
    localStorage.removeItem(buildLocalKey(userId));
    try {
      await api.delete(`/payments/drafts/${draftId}`);
      return true;
    } catch (error) {
      if (isNetworkOrRouteMissing(error)) {
        return true; // Local already cleaned up above
      }
      throw new Error(extractErrorMessage(error));
    }
  }
};

export default paymentDraftService;
